import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ExportsObjectDataRequest,
  ObjectsCreateObjectRequest,
  ObjectsDeleteManyObjectsRequest,
  ObjectsExportObjectsRequest,
  ObjectsUpsertObjectRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { objectKeys, type ObjectRecord } from './entity';
import { getObject } from './queries';

type ObjectAttributes = Record<string, string>;
const POLL_INTERVAL = 2_000;
const MAX_POLL_ATTEMPTS = 30;

function serializedAttributes(attributes: ObjectAttributes, hiddenAttributes: readonly string[] = []) {
  return { ...attributes, ...Object.fromEntries(hiddenAttributes.map((key) => [key, ''])) };
}

type WaitForObjectInput = {
  workspaceId: number;
  objectTypeId: string;
  identifier: string;
  expectedAttributes?: ObjectAttributes;
  pollMs?: number;
  maxAttempts?: number;
  getRecord?: typeof getObject;
};

export async function waitForObject({
  workspaceId,
  objectTypeId,
  identifier,
  expectedAttributes,
  pollMs = POLL_INTERVAL,
  maxAttempts = MAX_POLL_ATTEMPTS,
  getRecord = getObject,
}: WaitForObjectInput): Promise<ObjectRecord> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const record = await getRecord(workspaceId, objectTypeId, identifier);
      const isCurrent =
        expectedAttributes == null ||
        Object.entries(expectedAttributes).every(([key, value]) => (record.attributes[key] ?? '') === value);
      if (isCurrent) return record;
    } catch (error) {
      if (getApiClientErrorStatus(error) !== 404) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, pollMs));
  }
  throw new Error(`Timed out waiting for object ${identifier}.`);
}

export type CreateObjectInput = {
  workspaceId: number;
  objectTypeId: string;
  objectId: string;
  attributes: ObjectAttributes;
  hiddenAttributes?: readonly string[];
};

export async function createObject({
  workspaceId,
  objectTypeId,
  objectId,
  attributes,
  hiddenAttributes,
}: CreateObjectInput): Promise<ObjectRecord> {
  const savedAttributes = serializedAttributes(attributes, hiddenAttributes);
  const body: ObjectsCreateObjectRequest = { object: { object_id: objectId, attributes: savedAttributes } };
  await hydraUiApi.objects.createObjectTypesObjects({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId } },
    body,
  });
  return waitForObject({ workspaceId, objectTypeId, identifier: objectId, expectedAttributes: savedAttributes });
}

export type UpdateObjectInput = Omit<CreateObjectInput, 'objectId'> & { identifier: string };

export async function updateObject({
  workspaceId,
  objectTypeId,
  identifier,
  attributes,
  hiddenAttributes,
}: UpdateObjectInput): Promise<ObjectRecord> {
  const savedAttributes = serializedAttributes(attributes, hiddenAttributes);
  const body: ObjectsUpsertObjectRequest = { object: { attributes: savedAttributes } };
  await hydraUiApi.objects.updateObjectTypesObjects({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId, identifier } },
    body,
  });
  return waitForObject({ workspaceId, objectTypeId, identifier, expectedAttributes: savedAttributes });
}

export type DeleteObjectsFilter = NonNullable<ObjectsDeleteManyObjectsRequest['filters']>;

export type DeleteObjectsSelection =
  | { cioObjectIds: string[]; filters?: never; withoutRelationships?: never }
  | {
      cioObjectIds?: never;
      filters: DeleteObjectsFilter;
      withoutRelationships?: boolean;
    }
  | { cioObjectIds?: never; filters?: never; withoutRelationships: true };

export type DeleteObjectsInput = DeleteObjectsSelection & { workspaceId: number; objectTypeId: string };

export async function deleteObjects({
  workspaceId,
  objectTypeId,
  cioObjectIds,
  filters,
  withoutRelationships,
}: DeleteObjectsInput): Promise<void> {
  if ((cioObjectIds == null || cioObjectIds.length === 0) && filters == null && withoutRelationships !== true) {
    throw new Error('Cannot delete objects without a selection.');
  }
  const body = {
    cio_object_ids: cioObjectIds,
    filters,
    without_relationships: withoutRelationships,
  } satisfies ObjectsDeleteManyObjectsRequest;
  await hydraUiApi.objects.objectTypesObjectsDelete({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId } },
    body,
  });
}

export type ExportObjectsInput = {
  workspaceId: number;
  objectTypeId: string;
  body: ObjectsExportObjectsRequest;
};

export async function exportObjects({ workspaceId, objectTypeId, body }: ExportObjectsInput): Promise<void> {
  await hydraUiApi.objects.objectTypesObjectsExports({
    params: { path: { environment_id: String(workspaceId), type_id: objectTypeId } },
    body,
  });
}

export async function exportObjectData(workspaceId: number, body: ExportsObjectDataRequest) {
  return hydraUiApi.exports.objectData({ params: { path: { environment_id: String(workspaceId) } }, body });
}

type UseExportObjectsVariables = Omit<ExportObjectsInput, 'workspaceId'>;
export function useExportObjects(
  options: { mutationConfig?: HookMutationConfig<void, UseExportObjectsVariables> } = {},
) {
  const workspaceId = requireWorkspaceId();
  return useMutation({
    ...options.mutationConfig,
    mutationFn: (variables: UseExportObjectsVariables) => exportObjects({ workspaceId, ...variables }),
  });
}

type UseCreateObjectVariables = Omit<CreateObjectInput, 'workspaceId'>;
export function useCreateObject(
  options: { mutationConfig?: HookMutationConfig<ObjectRecord, UseCreateObjectVariables> } = {},
) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...mutationConfig } = options.mutationConfig ?? {};
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: UseCreateObjectVariables) => createObject({ workspaceId, ...variables }),
    onSuccess: (record, ...args) => {
      qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
      void qc.invalidateQueries({ queryKey: objectKeys.all(workspaceId) });
      onSuccess?.(record, ...args);
    },
  });
}

type UseUpdateObjectVariables = Omit<UpdateObjectInput, 'workspaceId'>;
export function useUpdateObject(
  options: { mutationConfig?: HookMutationConfig<ObjectRecord, UseUpdateObjectVariables> } = {},
) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...mutationConfig } = options.mutationConfig ?? {};
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: UseUpdateObjectVariables) => updateObject({ workspaceId, ...variables }),
    onSuccess: (record, ...args) => {
      qc.setQueryData(objectKeys.detail(workspaceId, record.id), record);
      void qc.invalidateQueries({ queryKey: objectKeys.all(workspaceId) });
      onSuccess?.(record, ...args);
    },
  });
}

type UseDeleteObjectsVariables = DeleteObjectsSelection & { objectTypeId: string };
export function useDeleteObjects(
  options: { mutationConfig?: HookMutationConfig<void, UseDeleteObjectsVariables> } = {},
) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...mutationConfig } = options.mutationConfig ?? {};
  return useMutation({
    ...mutationConfig,
    mutationFn: (variables: UseDeleteObjectsVariables) => deleteObjects({ workspaceId, ...variables }),
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: objectKeys.all(workspaceId) });
      onSuccess?.(...args);
    },
  });
}
